import React from "react";
import {
  FIELD_TYPE,
  SIMPLE_DECISIONS,
  FieldDecision,
  FormConfigField,
  WorkspaceTask,
} from "../shared/types";
import * as S from "../shared/styles";
import {
  MULTI_VALUE_SEPARATOR,
  OTHER_LABEL,
  allowsOther,
  correctionLabel,
  correctionOptions,
  hasCorrectionValue,
  isIncorrectDecision,
  isOtherValue,
  optionDetail,
  otherText,
  toOtherValue,
} from "../shared/helpers";
import InfoTip from "../shared/InfoTip";
import ReviewHeader, { ObjectNav } from "./ReviewHeader";

export type { ObjectNav };

interface FieldReviewTableProps {
  fields: FormConfigField[];
  task: WorkspaceTask;
  // shown value per field label (the record's, or the shown object's)
  values: Record<string, string>;
  decisions: Record<string, FieldDecision>;
  // decisions of the record, or of the object shown; key is the field label
  onDecide: (key: string, patch: Partial<FieldDecision>) => void;
  // set on object presence records: the object judged, shown under the question
  objectName?: string;
  objectNav?: ObjectNav;
  showError: boolean;
  disabled?: boolean;
}

const FieldReviewTable: React.FC<FieldReviewTableProps> = ({
  fields,
  task,
  values,
  decisions,
  onDecide,
  objectName,
  objectNav,
  showError,
  disabled = false,
}) => {
  const decisionOptions = SIMPLE_DECISIONS.map((v) => ({
    value: v,
    label: v === "correct" ? "Correct" : "Incorrect",
  }));

  const question = task.inspection_question?.trim() || null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0px" }}>
      <ReviewHeader question={question} objectName={objectName} objectNav={objectNav} />
      {fields.map((f) => {
        const key = f.label;
        const d: Partial<FieldDecision> = decisions[key] || {};
        const missingDecision = f.mandatory && !d.decision;
        const incorrect = isIncorrectDecision(d.decision);
        const missingCategory = incorrect && !hasCorrectionValue(f, d.issue_category);
        const hasError = showError && (missingDecision || missingCategory);

        return (
          <React.Fragment key={f.id}>
            <div style={{ ...fieldCard, ...(hasError ? { borderColor: "#fca5a5" } : {}) }}>
              {/* -- Row 1: field label -- */}
              <div style={fieldHeader}>
                <span style={{ ...fieldLabel, color: hasError ? "#ef4444" : "#374151" }}>
                  {f.label}
                  {f.mandatory && <span style={{ color: "#ef4444", marginLeft: "2px" }}>*</span>}
                </span>
              </div>

              {/* -- Row 2: value read from the field's source columns -- */}
              {f.type === FIELD_TYPE.CHECKBOX ? (
                <CheckboxValue
                  label={f.options[0]?.trim() || f.label}
                  checked={String(values[f.label] ?? "").toLowerCase() === "true"}
                />
              ) : (
                <div style={valueBox}>{values[f.label] ?? "\u2014"}</div>
              )}

              {/* -- Row 3: Review decision -- */}
              <div style={decisionRow}>
                <div style={decisionLabel}>Review decision</div>
                <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                  {decisionOptions.map((o) => {
                    const selected = d.decision === o.value;
                    return (
                      <label
                        key={o.value}
                        style={{
                          ...pillLabel,
                          ...(selected ? pillSelected : pillDefault),
                          ...(disabled ? { cursor: "default", opacity: 0.7 } : {}),
                          ...(missingDecision && showError && !selected
                            ? { borderColor: "#fca5a5" }
                            : {}),
                        }}
                      >
                        <input
                          type="radio"
                          name={`decision-${key}`}
                          value={o.value}
                          checked={selected}
                          disabled={disabled}
                          onChange={() => onDecide(key, { decision: o.value })}
                          style={{ display: "none" }}
                        />
                        {o.label}
                      </label>
                    );
                  })}
                </div>
              </div>

              {showError && missingDecision && (
                <div style={fieldError} role="alert">
                  Select a review decision.
                </div>
              )}

              {/* -- Row 4: Issue Category, only while this field is incorrect -- */}
              {incorrect && (
                <div style={correctionSection}>
                  <CorrectionInput
                    field={f}
                    name={`correction-${key}`}
                    value={d.issue_category ?? ""}
                    disabled={disabled}
                    hasError={showError && missingCategory}
                    onChange={(v) => onDecide(key, { issue_category: v })}
                  />
                  {showError && missingCategory && (
                    <div style={fieldError} role="alert">
                      {/* per part, as hasCorrectionValue checks: a multi-select joins its picks */}
                      {d.issue_category &&
                      d.issue_category.split(MULTI_VALUE_SEPARATOR).some(isOtherValue)
                        ? "Describe the Other category."
                        : `Select ${correctionLabel(f)}.`}
                    </div>
                  )}
                </div>
              )}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
};

// ----------------------------  CorrectionInput  ----------------------------

interface CorrectionInputProps {
  field: FormConfigField;
  // radio group name; unique per field (and per object on object presence records)
  name: string;
  value: string;
  disabled: boolean;
  hasError: boolean;
  onChange: (value: string) => void;
}

const CorrectionInput: React.FC<CorrectionInputProps> = ({
  field,
  name,
  value,
  disabled,
  hasError,
  onChange,
}) => {
  const errBorder = hasError ? "#ef4444" : "#d1d5db";
  const errColor = hasError ? "#ef4444" : "#6b7280";

  switch (field.type) {
    // -- Dropdown single: radio buttons from field.options --
    case FIELD_TYPE.DROPDOWN_SINGLE: {
      const opts = correctionOptions(field);
      const otherOn = !!value && isOtherValue(value);
      return (
        <fieldset
          style={{
            border: `1px solid ${errBorder}`,
            borderRadius: "6px",
            padding: "8px 10px",
            margin: 0,
          }}
        >
          <legend
            style={{ fontSize: "1.2rem", fontWeight: 600, color: errColor, padding: "0 4px" }}
          >
            {correctionLabel(field)}
            <span style={{ color: "#ef4444", marginLeft: "2px" }}>*</span>
            <InfoTip text={field.correction_help} label={correctionLabel(field)} />
          </legend>
          <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
            {opts.map((o) => (
              <label
                key={o}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "6px",
                  padding: "3px 0",
                  fontSize: "1.3rem",
                  color: "#374151",
                  cursor: disabled ? "default" : "pointer",
                  lineHeight: 1.4,
                }}
              >
                <input
                  type="radio"
                  name={name}
                  value={o}
                  checked={value === o}
                  disabled={disabled}
                  onChange={() => onChange(o)}
                  style={{ marginTop: "3px", flexShrink: 0 }}
                />
                <span style={{ wordBreak: "break-word" }}>
                  {o}
                  <InfoTip text={optionDetail(field, o)} label={o} />
                </span>
              </label>
            ))}
            {allowsOther(field) && (
              <OtherOption
                type="radio"
                name={name}
                checked={otherOn}
                text={otherOn ? otherText(value) : ""}
                disabled={disabled}
                hasError={hasError}
                onToggle={() => onChange(OTHER_LABEL)}
                onText={(t) => onChange(toOtherValue(t))}
              />
            )}
          </div>
        </fieldset>
      );
    }

    // -- Dropdown multi: checkboxes from field.options --
    case FIELD_TYPE.DROPDOWN_MULTI: {
      const opts = correctionOptions(field);
      const selected = value ? value.split(MULTI_VALUE_SEPARATOR) : [];
      const otherEntry = selected.find(isOtherValue);
      const toggle = (o: string) => {
        const next = selected.includes(o) ? selected.filter((s) => s !== o) : [...selected, o];
        onChange(next.join(MULTI_VALUE_SEPARATOR));
      };
      const toggleOther = () =>
        onChange(
          (otherEntry ? selected.filter((s) => s !== otherEntry) : [...selected, OTHER_LABEL]).join(
            MULTI_VALUE_SEPARATOR,
          ),
        );
      // The separator cannot appear inside the free text.
      const setOtherText = (t: string) =>
        onChange(
          selected
            .map((s) =>
              s === otherEntry ? toOtherValue(t.split(MULTI_VALUE_SEPARATOR).join("|")) : s,
            )
            .join(MULTI_VALUE_SEPARATOR),
        );
      return (
        <fieldset
          style={{
            border: `1px solid ${errBorder}`,
            borderRadius: "6px",
            padding: "8px 10px",
            margin: 0,
          }}
        >
          <legend
            style={{ fontSize: "1.2rem", fontWeight: 600, color: errColor, padding: "0 4px" }}
          >
            {correctionLabel(field)}
            <span style={{ color: "#ef4444", marginLeft: "2px" }}>*</span>
            <InfoTip text={field.correction_help} label={correctionLabel(field)} />
          </legend>
          <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
            {opts.map((o) => (
              <label
                key={o}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "6px",
                  padding: "3px 0",
                  fontSize: "1.3rem",
                  color: "#374151",
                  cursor: disabled ? "default" : "pointer",
                  lineHeight: 1.4,
                }}
              >
                <input
                  type="checkbox"
                  checked={selected.includes(o)}
                  disabled={disabled}
                  onChange={() => toggle(o)}
                  style={{ marginTop: "3px", flexShrink: 0 }}
                />
                <span style={{ wordBreak: "break-word" }}>
                  {o}
                  <InfoTip text={optionDetail(field, o)} label={o} />
                </span>
              </label>
            ))}
            {allowsOther(field) && (
              <OtherOption
                type="checkbox"
                checked={!!otherEntry}
                text={otherEntry ? otherText(otherEntry) : ""}
                disabled={disabled}
                hasError={hasError}
                onToggle={toggleOther}
                onText={setOtherText}
              />
            )}
          </div>
        </fieldset>
      );
    }

    // -- Numeric: number input with min/max/step --
    case FIELD_TYPE.NUMERIC:
      return (
        <div>
          <div
            style={{ fontSize: "1.2rem", fontWeight: 600, color: errColor, marginBottom: "4px" }}
          >
            {correctionLabel(field)}
            <span style={{ color: "#ef4444", marginLeft: "2px" }}>*</span>
            <InfoTip text={field.correction_help} label={correctionLabel(field)} />
          </div>
          <input
            type="number"
            value={value}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Enter correct number"
            style={{
              ...S.formInput,
              fontSize: "1.4rem",
              borderColor: errBorder,
              width: "180px",
            }}
          />
        </div>
      );

    // -- Text / default: textarea --
    default:
      return (
        <div>
          <div
            style={{ fontSize: "1.2rem", fontWeight: 600, color: errColor, marginBottom: "4px" }}
          >
            {correctionLabel(field)}
            <span style={{ color: "#ef4444", marginLeft: "2px" }}>*</span>
            <InfoTip text={field.correction_help} label={correctionLabel(field)} />
          </div>
          <textarea
            value={value}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Explain the evidence and the correct interpretation"
            rows={2}
            style={{
              ...S.formInput,
              fontSize: "1.4rem",
              borderColor: errBorder,
              resize: "vertical" as const,
              minHeight: "48px",
            }}
          />
        </div>
      );
  }
};

// ----------------------------  OtherOption  ----------------------------
// Built-in "Other" choice for allow_other dropdowns; reveals a free-text box when picked
// (the text is mandatory).

interface OtherOptionProps {
  type: "radio" | "checkbox";
  name?: string;
  checked: boolean;
  text: string;
  disabled?: boolean;
  hasError: boolean;
  onToggle: () => void;
  onText: (text: string) => void;
}

const OtherOption: React.FC<OtherOptionProps> = ({
  type,
  name,
  checked,
  text,
  disabled,
  hasError,
  onToggle,
  onText,
}) => (
  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
    <label style={{ ...optionRow(disabled), flexShrink: 0 }}>
      <input
        type={type}
        name={name}
        value={OTHER_LABEL}
        checked={checked}
        disabled={disabled}
        onChange={onToggle}
        style={{ marginTop: "3px", flexShrink: 0 }}
      />
      <span>{OTHER_LABEL}</span>
    </label>
    {checked && (
      <>
        <input
          type="text"
          value={text}
          disabled={disabled}
          autoFocus={!text}
          onChange={(e) => onText(e.target.value)}
          placeholder="Describe the other category (required)"
          aria-label="Other category"
          aria-required
          style={{
            ...S.formInput,
            fontSize: "1.3rem",
            width: "50%",
            flex: "0 1 50%",
            minWidth: "120px",
            borderColor: hasError && !text.trim() ? "#ef4444" : "#d1d5db",
          }}
        />
        <span
          style={{ color: "#ef4444", marginLeft: "-4px", flexShrink: 0 }}
          data-testid="other-required"
          aria-hidden="true"
        >
          *
        </span>
      </>
    )}
  </div>
);

const optionRow = (disabled?: boolean): React.CSSProperties => ({
  display: "flex",
  alignItems: "flex-start",
  gap: "6px",
  padding: "3px 0",
  fontSize: "1.3rem",
  color: "#374151",
  cursor: disabled ? "default" : "pointer",
  lineHeight: 1.4,
});

// ----------------------------  CheckboxValue  ----------------------------

const CheckboxValue: React.FC<{ label: string; checked: boolean }> = ({ label, checked }) => (
  <label
    style={{
      display: "flex",
      alignItems: "center",
      gap: "8px",
      padding: "6px 0",
      cursor: "default",
      fontSize: "1.3rem",
      color: "#374151",
    }}
  >
    <input
      type="checkbox"
      checked={checked}
      disabled
      style={{ width: "26px", height: "26px", accentColor: "#0f7b8a" }}
    />
    {label}
  </label>
);

// ----------------------------  Styles  ----------------------------

const fieldError: React.CSSProperties = {
  marginTop: "6px",
  fontSize: "1.2rem",
  color: "#ef4444",
};

const fieldCard: React.CSSProperties = {
  border: "1px solid #e5e7eb",
  borderRadius: "6px",
  padding: "14px 16px",
  marginBottom: "8px",
  background: "white",
  display: "flex",
  flexDirection: "column",
  gap: "10px",
};

const fieldHeader: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
};

const fieldLabel: React.CSSProperties = {
  fontWeight: 600,
  fontSize: "1.4rem",
};

const valueBox: React.CSSProperties = {
  background: "#f0fdfa",
  border: "1px solid #ccfbf1",
  borderRadius: "6px",
  padding: "10px 12px",
  fontSize: "1.3rem",
  lineHeight: 1.6,
  color: "#1f2937",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  maxHeight: "300px",
  overflowY: "auto",
};

const decisionRow: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
};

const decisionLabel: React.CSSProperties = {
  fontSize: "1.2rem",
  fontWeight: 600,
  color: "#6b7280",
  flexShrink: 0,
};

const pillLabel: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  padding: "5px 14px",
  borderRadius: "16px",
  fontSize: "1.3rem",
  fontWeight: 500,
  cursor: "pointer",
  transition: "all 0.15s",
  border: "1px solid transparent",
  userSelect: "none",
};

const pillDefault: React.CSSProperties = {
  background: "#f3f4f6",
  color: "#4b5563",
  borderColor: "#d1d5db",
};

const pillSelected: React.CSSProperties = {
  background: "#0f7b8a",
  color: "white",
  borderColor: "#0f7b8a",
};

const correctionSection: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  paddingTop: "4px",
  borderTop: "1px solid #f3f4f6",
};

export default FieldReviewTable;
